/**
 * DashboardPage.jsx
 *
 * Vue globale : KPIs, charts de valorisation et répartition, alertes contrats.
 * Utilise useContratPageData (ContratPageShared) pour toute la data.
 * Filtres via ContractFiltersBar (ContratPageShared).
 */

import { useMemo } from 'react';
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import AssignmentIcon from '@mui/icons-material/Assignment';
import EuroIcon from '@mui/icons-material/Euro';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts';

import {
  chartColors,
  ContractAlerts,
  ContractFiltersBar,
  formatCurrency,
  levelChartColors,
  levelKeys,
  levelLabels,
  useContratPageData,
} from './ContratPageShared';

// ── KPI Card ──────────────────────────────────────────────────────────────────

function KpiCard({ label, value, icon, color = 'primary', sub }) {
  return (
    <Card variant="outlined" sx={{ height: '100%' }}>
      <CardContent>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 1, fontSize: '0.7rem' }}>
              {label}
            </Typography>
            <Typography variant="h5" fontWeight={700} color={`${color}.main`} mt={0.5}>
              {value}
            </Typography>
            {sub && (
              <Typography variant="caption" color="text.secondary">{sub}</Typography>
            )}
          </Box>
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: 2,
              bgcolor: `${color}.50`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: `${color}.main`,
            }}
          >
            {icon}
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
}

// ── Main ──────────────────────────────────────────────────────────────────────

export default function ContratDashboard() {
  const {
    availableYears,
    clientAutocompleteOptions,
    clientHoursChartData,
    clientLevelChartData,
    competenceCatalog,
    customEndDate,
    customStartDate,
    dateMode,
    error,
    filteredContracts,
    mismatchContracts,
    monthlyValueChartData,
    rootOrganizationOptions,
    selectedInfogeranceFilter,
    selectedOrganizationId,
    selectedRootOrganizationId,
    selectedYear,
    setCustomEndDate,
    setCustomStartDate,
    setDateMode,
    setSelectedInfogeranceFilter,
    setSelectedOrganizationId,
    setSelectedRootOrganizationId,
    setSelectedYear,
    supportEntriesLoading,
    trackerChartData,
    userLevelChartData,
  } = useContratPageData();

  // ── KPIs globaux ────────────────────────────────────────────────────────

  const kpis = useMemo(() => {
    let totalContractValue = 0;
    let totalConsumedValue = 0;
    let overrunCount = 0;
    let infogeranceCount = 0;

    filteredContracts.forEach((c) => {
      infogeranceCount += c.infogerance ? 1 : 0;
    });

    monthlyValueChartData.forEach((row) => {
      totalContractValue +=
        (row.adminContractValue || 0) +
        (row.ingenieurContractValue || 0) +
        (row.pilotageContractValue || 0);
      totalConsumedValue +=
        (row.adminConsumedValue || 0) +
        (row.ingenieurConsumedValue || 0) +
        (row.pilotageConsumedValue || 0);
    });

    overrunCount = mismatchContracts.length;

    return {
      totalContracts: filteredContracts.length,
      infogeranceCount,
      totalContractValue,
      totalConsumedValue,
      rentaValue: totalContractValue - totalConsumedValue,
      rentaRate: totalContractValue > 0
        ? ((totalContractValue - totalConsumedValue) / totalContractValue) * 100
        : 0,
      overrunCount,
    };
  }, [filteredContracts, mismatchContracts, monthlyValueChartData]);

  // ── Pie data répartition par profil ─────────────────────────────────────

  const profilePieData = useMemo(() => {
    const totals = { admin: 0, ingenieur: 0, pilotage: 0 };
    monthlyValueChartData.forEach((row) => {
      totals.admin += row.adminConsumedValue || 0;
      totals.ingenieur += row.ingenieurConsumedValue || 0;
      totals.pilotage += row.pilotageConsumedValue || 0;
    });
    return [
      { name: 'Admin', value: totals.admin, color: levelChartColors.admin },
      { name: 'Ingénieur', value: totals.ingenieur, color: levelChartColors.ingenieur },
      { name: 'Pilotage', value: totals.pilotage, color: levelChartColors.pilotage },
    ].filter((d) => d.value > 0);
  }, [monthlyValueChartData]);

  return (
    <Box>
      {/* ── En-tête ── */}
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={2}>
        <Box>
          <Typography variant="h6" fontWeight={700}>Dashboard contrats</Typography>
          <Typography variant="caption" color="text.secondary">
            Vue globale des consommations, valorisations et alertes sur la période.
          </Typography>
        </Box>
        {supportEntriesLoading && (
          <Stack direction="row" alignItems="center" spacing={1}>
            <CircularProgress size={16} />
            <Typography variant="caption" color="text.secondary">Chargement des imputations…</Typography>
          </Stack>
        )}
      </Stack>

      {/* ── Filtres ── */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <ContractFiltersBar
          availableYears={availableYears}
          clientAutocompleteOptions={clientAutocompleteOptions}
          customEndDate={customEndDate}
          customStartDate={customStartDate}
          dateMode={dateMode}
          onChangeCustomEndDate={setCustomEndDate}
          onChangeCustomStartDate={setCustomStartDate}
          onChangeDateMode={setDateMode}
          onChangeInfogerance={setSelectedInfogeranceFilter}
          onChangeOrganization={setSelectedOrganizationId}
          onChangeRootOrganization={setSelectedRootOrganizationId}
          onChangeYear={setSelectedYear}
          rootOrganizationOptions={rootOrganizationOptions}
          selectedInfogeranceFilter={selectedInfogeranceFilter}
          selectedOrganizationId={selectedOrganizationId}
          selectedRootOrganizationId={selectedRootOrganizationId}
          selectedYear={selectedYear}
        />
      </Paper>

      {/* ── Alertes ── */}
      <ContractAlerts
        competenceCatalog={competenceCatalog}
        mismatchContracts={mismatchContracts}
        supportEntriesLoading={supportEntriesLoading}
      />

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* ── KPIs ── */}
      <Grid container spacing={2} mb={3}>
        <Grid item xs={6} md={3}>
          <KpiCard
            label="Contrats actifs"
            value={kpis.totalContracts}
            icon={<AssignmentIcon />}
            color="primary"
            sub={`dont ${kpis.infogeranceCount} infogérance`}
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <KpiCard
            label="Valeur contractuelle"
            value={formatCurrency(kpis.totalContractValue)}
            icon={<EuroIcon />}
            color="info"
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <KpiCard
            label="Rentabilité"
            value={`${kpis.rentaRate.toFixed(1)} %`}
            icon={kpis.rentaRate >= 0 ? <TrendingUpIcon /> : <TrendingDownIcon />}
            color={kpis.rentaRate >= 0 ? 'success' : 'error'}
            sub={formatCurrency(kpis.rentaValue)}
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <KpiCard
            label="Dépassements détectés"
            value={kpis.overrunCount}
            icon={<WarningAmberIcon />}
            color={kpis.overrunCount > 0 ? 'warning' : 'success'}
          />
        </Grid>
      </Grid>

      {/* ── Charts ── */}
      <Grid container spacing={2} mb={2}>

        {/* Valeur mensuelle contrat vs consommé */}
        <Grid item xs={12} lg={8}>
          <Paper sx={{ p: 2, height: '100%' }}>
            <Typography variant="subtitle2" fontWeight={600} mb={2}>
              Valeur mensuelle — contrat vs consommé
            </Typography>
            <Box sx={{ height: 280 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={monthlyValueChartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                  <YAxis tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} tick={{ fontSize: 11 }} />
                  <RechartsTooltip formatter={(v) => formatCurrency(v)} />
                  <Legend />
                  <Bar dataKey="adminContractValue" stackId="contract" fill="#90caf9" name="Admin (contrat)" />
                  <Bar dataKey="ingenieurContractValue" stackId="contract" fill="#a5d6a7" name="Inge (contrat)" />
                  <Bar dataKey="pilotageContractValue" stackId="contract" fill="#ffcc80" name="Pilotage (contrat)" />
                  <Bar dataKey="adminConsumedValue" stackId="consumed" fill="#1f4e5f" name="Admin (réel)" />
                  <Bar dataKey="ingenieurConsumedValue" stackId="consumed" fill="#4f772d" name="Inge (réel)" />
                  <Bar dataKey="pilotageConsumedValue" stackId="consumed" fill="#c97b2a" name="Pilotage (réel)" />
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </Paper>
        </Grid>

        {/* Répartition consommée par profil */}
        <Grid item xs={12} lg={4}>
          <Paper sx={{ p: 2, height: '100%' }}>
            <Typography variant="subtitle2" fontWeight={600} mb={2}>
              Répartition valeur consommée
            </Typography>
            <Box sx={{ height: 280, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {profilePieData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={profilePieData}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      outerRadius={90}
                      label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    >
                      {profilePieData.map((entry) => (
                        <Cell key={entry.name} fill={entry.color} />
                      ))}
                    </Pie>
                    <RechartsTooltip formatter={(v) => formatCurrency(v)} />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <Typography variant="body2" color="text.secondary">Pas de données</Typography>
              )}
            </Box>
          </Paper>
        </Grid>

        {/* Heures par client */}
        {clientHoursChartData.length > 0 && (
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="subtitle2" fontWeight={600} mb={2}>
                Heures d'intervention par client (Top 10)
              </Typography>
              <Box sx={{ height: 260 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={clientHoursChartData.slice(0, 10)} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" tick={{ fontSize: 11 }} />
                    <YAxis dataKey="name" type="category" width={120} tick={{ fontSize: 10 }} />
                    <RechartsTooltip formatter={(v) => `${Number(v).toFixed(2)} h`} />
                    <Legend />
                    <Bar dataKey="helpdesk" fill={chartColors[0]} name="Infogérance" />
                    <Bar dataKey="commande" fill={chartColors[2]} name="Hors infogérance" />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </Paper>
          </Grid>
        )}

        {/* Heures par tracker */}
        {trackerChartData.length > 0 && (
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="subtitle2" fontWeight={600} mb={2}>
                Heures par tracker
              </Typography>
              <Box sx={{ height: 260 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={trackerChartData.slice(0, 8)} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" tick={{ fontSize: 11 }} />
                    <YAxis dataKey="name" type="category" width={120} tick={{ fontSize: 10 }} />
                    <RechartsTooltip formatter={(v) => `${Number(v).toFixed(2)} h`} />
                    <Bar dataKey="value" name="Heures">
                      {trackerChartData.map((_, i) => (
                        <Cell key={i} fill={chartColors[i % chartColors.length]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </Paper>
          </Grid>
        )}

        {/* Heures par intervenant */}
        {userLevelChartData.length > 0 && (
          <Grid item xs={12}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="subtitle2" fontWeight={600} mb={2}>
                Heures par intervenant (Infogérance) — Top 15
              </Typography>
              <Box sx={{ height: 280 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={userLevelChartData.slice(0, 15)}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <RechartsTooltip formatter={(v) => `${Number(v).toFixed(2)} h`} />
                    <Legend />
                    {levelKeys.map((key) => (
                      <Bar key={key} dataKey={key} stackId="stack" fill={levelChartColors[key]} name={levelLabels[key]} />
                    ))}
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </Paper>
          </Grid>
        )}
      </Grid>

      {/* ── Footer ── */}
      <Divider sx={{ my: 1 }} />
      <Typography variant="caption" color="text.secondary">
        {filteredContracts.length} contrat(s) pris en compte sur la période.
      </Typography>
    </Box>
  );
}